import { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { TokenService } from "../../../../../lib/tokenService";
import { sendToTerminal } from "../../../../../lib/terminalLogger";
import { LogCategory } from "../../../../../config/logging";
import {
	createOrcamentoFromQuotation,
	generatePixQrCode,
} from "../../services/pedidoPixService";
import {
	markSourceItemsOutsourced,
	persistCrossTenantLink,
} from "../../services/crossTenantLinkService";
import { QuotationOperationContextStore } from "../../services/quotationOperationContext";
import {
	uploadQuotationComprovante,
} from "../../services/quotationDocumentsService";
import {
	finalizeQuotationOrder,
	listQuotationPaymentMethods,
	processQuotationFinancialApproval,
} from "../../services/paymentClosingService";
import type {
	CreatedOrderRuntimeData,
	CreatedQuotationOrderData,
	NotifyFn,
	QuotationClosingFormData,
	QuotationSummaryDependencies,
} from "./QuotationSummary.types";
import {
	AUTO_PIX_STORAGE_KEY,
	buildClosingChecklist,
	getClosingActionHint,
	getClosingStatusLabel,
	getComprovanteSectionTooltip,
	getInitialAutoPixEnabled,
	getPaymentMethodHint,
	getPostSaveSuccessNotification,
	getSaveFileButtonHint,
	getSaveNowHint,
} from "./QuotationSummary.utils";
import {
	getPaymentFlowKind,
	isComprovanteRequired,
	shouldShowComprovanteUpload,
} from "../../services/paymentFlowKind";
import { executeQuotationQuickSave } from "../../services/quotationQuickSaveService";
import { requestConfirmation } from "../../../../../lib/globalConfirm";
import { DocumentService } from "../../../../../lib/corel/actions/document";

interface UseQuotationClosingFlowParams extends QuotationSummaryDependencies {
	notify: NotifyFn;
	onOrderCreated?: (data: CreatedQuotationOrderData) => void;
	onSaveNowRequested?: (data: CreatedQuotationOrderData) => void;
}

const createDefaultClosingForm = (): QuotationClosingFormData => ({
	observacaoPedido: "",
	observacaoItem: "",
	entrega: "normal",
	exibirRecibo: false,
	precisaNotaFiscal: false,
	temTransportadora: false,
	comprovanteFiles: [],
});

export function useQuotationClosingFlow({
	objects,
	selectedClient,
	finalClientName,
	selectedRule,
	selectedQuality,
	savingRule,
	quotationResult,
	descontoAplicado,
	finalMetragem,
	isMetros,
	totalMetragem,
	totalUnidades,
	notify,
	onOrderCreated,
	onSaveNowRequested,
}: UseQuotationClosingFlowParams) {
	const [autoGeneratePix, setAutoGeneratePixState] =
		useState<boolean>(getInitialAutoPixEnabled);
	const [isCreatingOrder, setIsCreatingOrder] = useState(false);
	const [isGeneratingPix, setIsGeneratingPix] = useState(false);
	const [isSavingClosingData, setIsSavingClosingData] = useState(false);
	const [isQuickSaving, setIsQuickSaving] = useState(false);
	const [isUploadingComprovante, setIsUploadingComprovante] = useState(false);
	const [createdOrderData, setCreatedOrderData] =
		useState<CreatedOrderRuntimeData | null>(null);
	const [createdOrderPayload, setCreatedOrderPayload] =
		useState<CreatedQuotationOrderData | null>(null);
	const [closingState, setClosingState] = useState<
		"draft" | "registered" | "pix_generated" | "pending_financial_approval"
	>("draft");
	const [isClosingModalOpen, setIsClosingModalOpen] = useState(false);
	const [closingForm, setClosingForm] = useState<QuotationClosingFormData>(
		createDefaultClosingForm,
	);
	const [createError, setCreateError] = useState<string | null>(null);
	const [closingDataError, setClosingDataError] = useState<string | null>(null);
	const [hasSavedClosingData, setHasSavedClosingData] = useState(false);
	const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
	const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | null>(null);
	const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
	const [paymentMethodsError, setPaymentMethodsError] = useState<string | null>(null);
	const [isProcessingFinancial, setIsProcessingFinancial] = useState(false);
	const [pixData, setPixData] = useState<{
		txid: string;
		brcode: string;
		idPagamento?: number;
	} | null>(null);
	const [pixError, setPixError] = useState<string | null>(null);
	const [pixCopiedFeedback, setPixCopiedFeedback] = useState(false);
	// Persistente (diferente do feedback transitório): vira true ao copiar o
	// código PIX e habilita o fechamento. Reseta ao (re)gerar/limpar o PIX.
	const [hasCopiedPix, setHasCopiedPix] = useState(false);
	const [isPixModalOpen, setIsPixModalOpen] = useState(false);
	const [pixQrImageUrl, setPixQrImageUrl] = useState<string | null>(null);
	const [comprovanteStatus, setComprovanteStatus] = useState<
		"idle" | "uploading" | "success" | "error"
	>("idle");
	const [comprovanteError, setComprovanteError] = useState<string | null>(null);
	const [comprovanteUploadedCount, setComprovanteUploadedCount] = useState(0);
	// Vínculo cross-tenant: contribuintes de origem (oneuniformes) confirmados no
	// gate antes da criação, quando cliente roteado. Lista para suportar N->1
	// (SPEC-15 RN-04). Repassado ao persistCrossTenantLink.
	const [sourceLink, setSourceLink] = useState<
		Array<{ orcId: number; oitId: number; role?: string | null }>
	>([]);

	const resetClosingForm = useCallback(() => {
		setClosingForm(createDefaultClosingForm());
		setCreateError(null);
		setClosingDataError(null);
		setHasSavedClosingData(false);
		setComprovanteStatus("idle");
		setComprovanteError(null);
		setComprovanteUploadedCount(0);
	}, []);

	const resetClosingFlowState = useCallback(() => {
		setCreatedOrderData(null);
		setCreatedOrderPayload(null);
		setClosingState("draft");
		setIsClosingModalOpen(false);
		setPaymentMethods([]);
		setSelectedPaymentMethodId(null);
		setPaymentMethodsError(null);
		setPixData(null);
		setPixError(null);
		setPixCopiedFeedback(false);
		setHasCopiedPix(false);
		setIsPixModalOpen(false);
		setPixQrImageUrl(null);
		setSourceLink([]);
		resetClosingForm();
	}, [resetClosingForm]);

	const resetClosingDetailsPreservingPayment = useCallback(() => {
		setCreatedOrderData(null);
		setCreatedOrderPayload(null);
		setClosingState("draft");
		setIsClosingModalOpen(false);
		setCreateError(null);
		setClosingDataError(null);
		setHasSavedClosingData(false);
		setPixData(null);
		setPixError(null);
		setPixCopiedFeedback(false);
		setHasCopiedPix(false);
		setIsPixModalOpen(false);
		setPixQrImageUrl(null);
		setClosingForm(createDefaultClosingForm());
		setComprovanteStatus("idle");
		setComprovanteError(null);
		setComprovanteUploadedCount(0);
	}, []);

	const setAutoGeneratePix = useCallback((enabled: boolean) => {
		setAutoGeneratePixState(enabled);

		if (typeof window === "undefined") return;

		try {
			window.localStorage.setItem(
				AUTO_PIX_STORAGE_KEY,
				enabled ? "1" : "0",
			);
		} catch {
			// Storage failure should not block the flow.
		}
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const syncAutoPixPreference = () => {
			const nextValue = getInitialAutoPixEnabled();
			setAutoGeneratePixState((currentValue) =>
				currentValue === nextValue ? currentValue : nextValue,
			);
		};

		syncAutoPixPreference();
		window.addEventListener("focus", syncAutoPixPreference);
		window.addEventListener("storage", syncAutoPixPreference);
		document.addEventListener("visibilitychange", syncAutoPixPreference);

		return () => {
			window.removeEventListener("focus", syncAutoPixPreference);
			window.removeEventListener("storage", syncAutoPixPreference);
			document.removeEventListener("visibilitychange", syncAutoPixPreference);
		};
	}, []);

	useEffect(() => {
		let isCancelled = false;
		const loadPaymentMethods = async () => {
			try {
				setIsLoadingPaymentMethods(true);
				setPaymentMethodsError(null);
				const methods = await listQuotationPaymentMethods();
				if (isCancelled) return;
				setPaymentMethods(methods);
			} catch (err) {
				if (isCancelled) return;

				const errorMessage =
					err instanceof Error
						? err.message
						: "Falha ao carregar formas de pagamento";

				setPaymentMethods([]);
				setPaymentMethodsError(errorMessage);

				notify({
					type: "warning",
					title: "Formas de pagamento indisponiveis",
					message: errorMessage,
					duration: 6000,
				});
			} finally {
				if (!isCancelled) setIsLoadingPaymentMethods(false);
			}
		};

		void loadPaymentMethods();
		return () => {
			isCancelled = true;
		};
	}, [notify]);

	useEffect(() => {
		if (!createdOrderData || createdOrderData.oitId !== null) return;

		sendToTerminal(
			"[QUOTATION] Registro sem oit_id no retorno principal",
			{ orcId: createdOrderData.orcId },
			LogCategory.ERROR,
		);
	}, [createdOrderData]);

	const selectedPaymentMethod = useMemo(() => {
		if (selectedPaymentMethodId === null) return null;
		return paymentMethods.find((method) => method.id === selectedPaymentMethodId) || null;
	}, [paymentMethods, selectedPaymentMethodId]);

	const paymentFlowKind = useMemo(
		() => getPaymentFlowKind(selectedPaymentMethodId),
		[selectedPaymentMethodId],
	);
	const isPixPaymentMethodSelected = paymentFlowKind === "sys_pix_qr";
	const showComprovanteSection = shouldShowComprovanteUpload(paymentFlowKind);
	const comprovanteRequired = isComprovanteRequired(paymentFlowKind);
	const hasComprovanteAttached = closingForm.comprovanteFiles.length > 0;
	const hasSelectedClientDocument = !!selectedClient?.cnpjCpf?.trim();

	const closingChecklist = useMemo(
		() =>
			buildClosingChecklist({
				flowKind: paymentFlowKind,
				hasPaymentMethod: selectedPaymentMethodId !== null,
				hasGeneratedPix: !!pixData?.brcode,
				hasCopiedPix: hasCopiedPix,
				hasComprovante: hasComprovanteAttached,
			}),
		[
			hasComprovanteAttached,
			hasCopiedPix,
			paymentFlowKind,
			pixData?.brcode,
			selectedPaymentMethodId,
		],
	);

	const closingStatusLabel = useMemo(
		() =>
			getClosingStatusLabel({
				hasSavedFile: hasSavedClosingData,
				flowKind: paymentFlowKind,
			}),
		[hasSavedClosingData, paymentFlowKind],
	);

	const comprovanteSectionTooltip = useMemo(
		() => getComprovanteSectionTooltip(paymentFlowKind),
		[paymentFlowKind],
	);
	const paymentMethodHint = useMemo(
		() => getPaymentMethodHint(selectedPaymentMethod),
		[selectedPaymentMethod],
	);
	const closingActionHint = useMemo(
		() =>
			getClosingActionHint(
				selectedPaymentMethod,
				paymentFlowKind,
				hasSelectedClientDocument,
			),
		[selectedPaymentMethod, paymentFlowKind, hasSelectedClientDocument],
	);
	const saveNowHint = useMemo(
		() => getSaveNowHint(createdOrderPayload?.oitId),
		[createdOrderPayload?.oitId],
	);

	useEffect(() => {
		const brcode = pixData?.brcode?.trim();
		if (!brcode) {
			setPixQrImageUrl(null);
			return;
		}

		let isCancelled = false;

		const generateQrCode = async () => {
			try {
				const dataUrl = await QRCode.toDataURL(brcode, {
					width: 240,
					margin: 1,
					errorCorrectionLevel: "M",
				});

				if (isCancelled) return;
				setPixQrImageUrl(dataUrl);
			} catch (err) {
				if (isCancelled) return;

				const errorMessage =
					err instanceof Error ? err.message : "Falha ao renderizar QR Code PIX";

				sendToTerminal(
					"[QUOTATION] Erro ao gerar imagem local do QR Code PIX",
					{
						error: errorMessage,
						brcodeLength: brcode.length,
					},
					LogCategory.ERROR,
				);
				setPixQrImageUrl(null);
			}
		};

		void generateQrCode();

		return () => {
			isCancelled = true;
		};
	}, [pixData?.brcode]);

	const canGeneratePix =
		!!createdOrderData &&
		!isGeneratingPix &&
		!isCreatingOrder &&
		isPixPaymentMethodSelected &&
		hasSelectedClientDocument;
	const hasGeneratedPix = !!pixData?.brcode;

	// Salvar arquivo: unica acao final — exige pre-requisitos conforme a forma.
	const canSaveClosing =
		!!selectedPaymentMethodId &&
		!isSavingClosingData &&
		!hasSavedClosingData &&
		(paymentFlowKind !== "sys_pix_qr" ||
			(hasGeneratedPix && hasCopiedPix)) &&
		(paymentFlowKind !== "pix_comprovante" || hasComprovanteAttached);

	const saveClosingHint = getSaveFileButtonHint({
		flowKind: paymentFlowKind,
		hasPaymentMethod: selectedPaymentMethodId !== null,
		hasGeneratedPix,
		hasCopiedPix,
		hasComprovante: hasComprovanteAttached,
		hasSavedFile: hasSavedClosingData,
	});

	// Tooltip do botão de PIX (gerar/ver/erro de documento).
	const generatePixHint =
		isPixPaymentMethodSelected && !hasSelectedClientDocument
			? "Cliente sem CPF/CNPJ cadastrado — o PIX exige documento do cliente."
			: hasGeneratedPix
				? "Reabrir o QR Code PIX gerado."
				: "Gera a cobrança PIX (QR Code + copia e cola).";

	const canProcessFinancial =
		!!createdOrderData &&
		!!selectedPaymentMethod &&
		!isPixPaymentMethodSelected &&
		!isProcessingFinancial;
	const canQuickSave =
		!!createdOrderPayload?.oitId &&
		!!selectedClient?.id &&
		!isQuickSaving &&
		!isSavingClosingData;
	const canCreateOrder =
		!!quotationResult &&
		!!selectedClient?.id &&
		!!selectedPaymentMethodId &&
		!isCreatingOrder;

	const setClosingField = useCallback(
		<K extends keyof QuotationClosingFormData>(
			field: K,
			value: QuotationClosingFormData[K],
		) => {
			setClosingForm((prev) => {
				const next = { ...prev, [field]: value };
				return next;
			});

			if (field === "comprovanteFiles") {
				setComprovanteError(null);
				setComprovanteStatus("idle");
			}
			if (field === "observacaoPedido" || field === "observacaoItem") {
				setCreateError(null);
			}
		},
		[],
	);

	const removeClosingFile = useCallback(
		(type: "comprovanteFiles", index: number) => {
			setClosingForm((prev) => ({
				...prev,
				[type]: prev[type].filter((_, fileIndex) => fileIndex !== index),
			}));
			if (type === "comprovanteFiles") {
				setComprovanteError(null);
				setComprovanteStatus("idle");
			}
		},
		[],
	);

	const openClosingModal = useCallback(() => {
		if (!createdOrderData) {
			return;
		}
		setCreateError(null);
		setClosingDataError(null);
		setIsClosingModalOpen(true);
	}, [createdOrderData]);

	const validateDraftForm = useCallback((): string | null => {
		if (!selectedClient?.id) {
			return "Selecione um cliente cadastrado para criar o pedido.";
		}

		// Forma de pagamento passou para o passo 2 (fechamento) — não é exigida no registro.
		return null;
	}, [selectedClient?.id]);

	const validateClosingData = useCallback((): string | null => {
		if (!createdOrderData) {
			return "Registre o orcamento antes de salvar o arquivo.";
		}

		if (!selectedPaymentMethodId) {
			return "Selecione a forma de pagamento para salvar o arquivo.";
		}

		if (paymentFlowKind === "sys_pix_qr") {
			if (!hasGeneratedPix) {
				return "Gere o PIX antes de salvar o arquivo.";
			}
			if (!hasCopiedPix) {
				return "Copie o codigo PIX antes de salvar o arquivo.";
			}
		}

		if (paymentFlowKind === "pix_comprovante" && !hasComprovanteAttached) {
			return "Anexe o comprovante da transferencia PIX antes de salvar o arquivo.";
		}

		return null;
	}, [
		createdOrderData,
		hasComprovanteAttached,
		hasCopiedPix,
		hasGeneratedPix,
		paymentFlowKind,
		selectedPaymentMethodId,
	]);

	const uploadComprovante = useCallback(
		async (orcId: number, userId: number): Promise<boolean> => {
			if (closingForm.comprovanteFiles.length === 0) {
				return true;
			}

			try {
				setIsUploadingComprovante(true);
				setComprovanteStatus("uploading");
				setComprovanteError(null);
				const result = await uploadQuotationComprovante({
					orcId,
					userId,
					files: closingForm.comprovanteFiles,
				});
				setComprovanteStatus("success");
				setComprovanteUploadedCount(result.uploadedCount);
				return true;
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Falha ao enviar comprovante.";
				setComprovanteStatus("error");
				setComprovanteError(errorMessage);
				notify({
					type: "warning",
					title: "Falha no comprovante",
					message: errorMessage,
					duration: 7000,
				});
				return false;
			} finally {
				setIsUploadingComprovante(false);
			}
		},
		[closingForm.comprovanteFiles, notify],
	);

	const handleRetryComprovante = useCallback(async () => {
		if (!createdOrderData) return;
		await uploadComprovante(createdOrderData.orcId, createdOrderData.userId);
	}, [createdOrderData, uploadComprovante]);

	const handleSelectPaymentMethod = useCallback(
		(paymentMethodId: number | null) => {
			const method = paymentMethods.find((item) => item.id === paymentMethodId);
			setSelectedPaymentMethodId(paymentMethodId);
			setPixData(null);
			setPixError(null);
			setPixCopiedFeedback(false);
			setHasCopiedPix(false);
			setClosingForm((prev) => ({
				...prev,
				comprovanteFiles: [],
			}));
			setComprovanteStatus("idle");
			setComprovanteError(null);
			setComprovanteUploadedCount(0);

			if (paymentMethodId === null) return;
			sendToTerminal(
				"[QUOTATION_PAYMENT:SELECT_METHOD] Forma de pagamento selecionada",
				{
					paymentMethodId,
					paymentMethod: method?.descricao || "(desconhecida)",
				},
				LogCategory.BUSINESS,
			);
		},
		[paymentMethods],
	);

	const handleGeneratePix = useCallback(
		async (orderOverride?: CreatedOrderRuntimeData) => {
			const orderData = orderOverride || createdOrderData;
			if (!orderData) {
				sendToTerminal(
					"[QUOTATION] Geracao de PIX bloqueada: pedido ausente",
					{
						hasOverride: !!orderOverride,
						hasCreatedOrderData: !!createdOrderData,
					},
					LogCategory.ERROR,
				);
				notify({
					type: "warning",
					title: "Pedido nao encontrado",
					message: "Crie o pedido antes de gerar o PIX.",
					duration: 4500,
				});
				return;
			}

			if (!orderOverride && !isPixPaymentMethodSelected) {
				sendToTerminal(
					"[QUOTATION] Geracao manual de PIX bloqueada: forma SYS.PIX nao selecionada",
					{
						orcId: orderData.orcId,
						selectedPaymentMethodId: selectedPaymentMethod?.id ?? null,
					},
					LogCategory.ERROR,
				);
				notify({
					type: "warning",
					title: "Selecione SYS.PIX",
					message: "Escolha a forma SYS.PIX antes de gerar o QR Code manualmente.",
					duration: 5000,
				});
				return;
			}

			if (!selectedClient?.id) {
				sendToTerminal(
					"[QUOTATION] Geracao de PIX bloqueada: cliente ausente",
					{ hasSelectedClient: !!selectedClient },
					LogCategory.ERROR,
				);
				notify({
					type: "warning",
					title: "Cliente obrigatorio",
					message: "Selecione um cliente para gerar o PIX.",
					duration: 4500,
				});
				return;
			}

			const clientDocument = selectedClient.cnpjCpf?.trim() || "";
			if (!clientDocument) {
				sendToTerminal(
					"[QUOTATION] Geracao de PIX bloqueada: cliente sem documento",
					{ clientId: selectedClient.id },
					LogCategory.ERROR,
				);
				const msg =
					"Cliente sem CPF/CNPJ cadastrado. Atualize o cadastro para gerar PIX.";
				setPixError(msg);
				notify({
					type: "warning",
					title: "Documento obrigatorio",
					message: msg,
					duration: 7000,
				});
				return;
			}

			try {
				setIsGeneratingPix(true);
				setPixError(null);
				// Novo brcode -> precisa copiar de novo antes de fechar.
				setHasCopiedPix(false);

				sendToTerminal(
					"[QUOTATION] Iniciando geracao de PIX na tela de resumo",
					{
						orcId: orderData.orcId,
						clientId: Number(selectedClient.id),
						totalValue: orderData.totalValue,
						autoGeneratePix,
					},
					LogCategory.BUSINESS,
				);

				const pix = await generatePixQrCode({
					orcId: orderData.orcId,
					userId: orderData.userId,
					clientId: Number(selectedClient.id),
					totalValue: orderData.totalValue,
					clientName:
						finalClientName ||
						selectedClient.empresa ||
						selectedClient.nome ||
						"Cliente",
					clientDocument,
				});

				setPixData({
					txid: pix.txid,
					brcode: pix.brcode,
					idPagamento: pix.idPagamento,
				});
				setClosingState("pix_generated");
				setIsClosingModalOpen(false);
				setIsPixModalOpen(true);

				sendToTerminal(
					"[QUOTATION] PIX gerado com sucesso",
					{
						orcId: orderData.orcId,
						txid: pix.txid,
						idPagamento: pix.idPagamento || null,
					},
					LogCategory.BUSINESS,
				);

				notify({
					type: "success",
					title: "PIX gerado",
					message: `PIX criado para o orcamento #${orderData.orcId}.`,
					duration: 5000,
				});
			} catch (err) {
				const errorMessage =
					err instanceof Error ? err.message : "Falha ao gerar PIX";
				setPixError(errorMessage);

				sendToTerminal(
					"[QUOTATION] Erro ao gerar PIX",
					{ orcId: orderData.orcId, error: errorMessage },
					LogCategory.ERROR,
				);

				notify({
					type: "error",
					title: "Erro ao gerar PIX",
					message: errorMessage,
					duration: 7000,
				});
			} finally {
				setIsGeneratingPix(false);
			}
		},
		[
			autoGeneratePix,
			createdOrderData,
			finalClientName,
			isPixPaymentMethodSelected,
			notify,
			selectedClient,
			selectedPaymentMethod,
		],
	);

	const handleSaveClosingData = useCallback(async () => {
		const validationError = validateClosingData();
		if (validationError) {
			setClosingDataError(validationError);
			notify({
				type: "warning",
				title: "Fechamento indisponível",
				message: validationError,
				duration: 4500,
			});
			return;
		}

		if (!createdOrderData || !createdOrderPayload) {
			return;
		}

		try {
			setIsSavingClosingData(true);
			setClosingDataError(null);

			// Persiste fechamento (forma, recibo, NF, transportadora, observação) e
			// avança o pedido para ost_id=2 via rota existente updateOrcamentoFinal.
			await finalizeQuotationOrder({
				orcId: createdOrderData.orcId,
				userId: createdOrderData.userId,
				clientName:
					finalClientName ||
					selectedClient?.empresa ||
					selectedClient?.nome ||
					"Cliente",
				formaPagamentoId: selectedPaymentMethodId,
				exibirRecibo: closingForm.exibirRecibo,
				precisaNotaFiscal: closingForm.precisaNotaFiscal,
				temTransportadora: closingForm.temTransportadora,
				observacaoPedido: closingForm.observacaoPedido,
			});

			const comprovanteUploaded = await uploadComprovante(
				createdOrderData.orcId,
				createdOrderData.userId,
			);

			if (comprovanteRequired && !comprovanteUploaded) {
				setClosingDataError(
					"Nao foi possivel enviar o comprovante. Corrija e tente salvar o arquivo novamente.",
				);
				return;
			}

			const payload: CreatedQuotationOrderData = {
				...createdOrderPayload,
				observacaoPedido: closingForm.observacaoPedido.trim(),
				observacaoItem: closingForm.observacaoItem.trim(),
				entrega: closingForm.entrega,
				exibirRecibo: closingForm.exibirRecibo,
				formaPagamentoId: selectedPaymentMethodId,
				formaPagamentoDescricao: selectedPaymentMethod?.descricao || null,
			};

			setCreatedOrderPayload(payload);
			setHasSavedClosingData(true);
			setIsClosingModalOpen(false);
			setIsPixModalOpen(false);

			if (paymentFlowKind === "sys_pix_qr") {
				setClosingState("pix_generated");
			} else if (paymentFlowKind === "pix_comprovante") {
				setClosingState("pending_financial_approval");
			} else {
				setClosingState("registered");
			}

			const successNotification = getPostSaveSuccessNotification(
				createdOrderData.orcId,
				paymentFlowKind,
			);
			notify({
				type: "success",
				title: successNotification.title,
				message: successNotification.message,
				duration: 5500,
			});

			// Continuação do fluxo: dispara o pipeline do Corel (salvar pedido +
			// carregamento das demais páginas). É o mesmo mecanismo que o
			// "Salvar arquivo" usava — aqui após finalizar o pedido.
			if (payload.oitId && onSaveNowRequested) {
				onSaveNowRequested({ ...payload, autoOpenPipeline: true });
			} else {
				onOrderCreated?.(payload);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Falha ao salvar fechamento.";
			setClosingDataError(errorMessage);
			notify({
				type: "error",
				title: "Erro ao salvar fechamento",
				message: errorMessage,
				duration: 7000,
			});
		} finally {
			setIsSavingClosingData(false);
		}
	}, [
		closingForm.entrega,
		closingForm.exibirRecibo,
		closingForm.observacaoItem,
		closingForm.observacaoPedido,
		closingForm.precisaNotaFiscal,
		closingForm.temTransportadora,
		createdOrderData,
		createdOrderPayload,
		finalClientName,
		notify,
		onOrderCreated,
		onSaveNowRequested,
		selectedClient,
		selectedPaymentMethod,
		selectedPaymentMethodId,
		comprovanteRequired,
		paymentFlowKind,
		uploadComprovante,
		validateClosingData,
	]);

	const handleProcessFinancialApproval = useCallback(async () => {
		if (!createdOrderData) {
			notify({
				type: "warning",
				title: "Orcamento nao encontrado",
				message: "Registre o orcamento antes de enviar para aprovacao financeira.",
				duration: 4500,
			});
			return;
		}

		if (!selectedClient?.id) {
			notify({
				type: "warning",
				title: "Cliente obrigatorio",
				message: "Selecione um cliente cadastrado para continuar.",
				duration: 4500,
			});
			return;
		}

		if (!selectedPaymentMethod) {
			notify({
				type: "warning",
				title: "Forma de pagamento obrigatoria",
				message: "Selecione uma forma de pagamento manual antes de enviar.",
				duration: 4500,
			});
			return;
		}

		try {
			setIsProcessingFinancial(true);
			const result = await processQuotationFinancialApproval({
				orcId: createdOrderData.orcId,
				userId: createdOrderData.userId,
				totalValue: createdOrderData.totalValue,
				clientId: Number(selectedClient.id),
				clientName:
					finalClientName ||
					selectedClient.empresa ||
					selectedClient.nome ||
					"Cliente",
				paymentMethod: selectedPaymentMethod,
			});

			setClosingState("pending_financial_approval");
			notify({
				type: "success",
				title: "Aprovacao financeira enviada",
				message:
					result.statusPagamento === "Pago"
						? `Orcamento #${createdOrderData.orcId} processado com ${selectedPaymentMethod.descricao}.`
						: `Orcamento #${createdOrderData.orcId} enviado para acompanhamento financeiro.`,
				duration: 5500,
			});
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: "Falha ao processar aprovacao financeira";

			sendToTerminal(
				"[QUOTATION_PAYMENT:PROCESS_FINANCIAL] Falha no fechamento financeiro",
				{
					orcId: createdOrderData.orcId,
					paymentMethodId: selectedPaymentMethod.id,
					error: errorMessage,
				},
				LogCategory.ERROR,
			);

			notify({
				type: "error",
				title: "Erro na aprovacao financeira",
				message: errorMessage,
				duration: 7000,
			});
		} finally {
			setIsProcessingFinancial(false);
		}
	}, [createdOrderData, finalClientName, notify, selectedClient, selectedPaymentMethod]);

	const handleQuickSaveToGerenciador = useCallback(async () => {
		if (!createdOrderPayload) {
			notify({
				type: "warning",
				title: "Orcamento nao encontrado",
				message: "Registre o orcamento antes de salvar.",
				duration: 4500,
			});
			return;
		}

		if (!createdOrderPayload.oitId) {
			notify({
				type: "warning",
				title: "Item indisponivel",
				message: "Aguarde o item do orcamento antes de salvar no Corel.",
				duration: 4500,
			});
			return;
		}

		// Cliente roteado: usa o usuário resolvido no tenant destino.
		const operationContext = QuotationOperationContextStore.get();
		const userData = TokenService.getUserData()?.data?.usuario;
		const userId = operationContext
			? operationContext.routing.targetUserId
			: userData?.id;
		const userName = operationContext
			? operationContext.routing.targetUserLogin
			: userData?.nome || "Usuario";

		if (!userId) {
			notify({
				type: "error",
				title: "Sessao invalida",
				message: "Nao foi possivel identificar o usuario logado.",
				duration: 5000,
			});
			return;
		}

		setIsQuickSaving(true);
		try {
			sendToTerminal(
				"[QUOTATION] Salvar no Corel e manter orcamento (Gerenciador)",
				{
					orcId: createdOrderPayload.orcId,
					oitId: createdOrderPayload.oitId,
				},
				LogCategory.BUSINESS,
			);

			const result = await executeQuotationQuickSave({
				createdOrderData: createdOrderPayload,
				selectedClient: selectedClient!,
				selectedRule,
				selectedQuality,
				objects,
				userId: Number(userId),
				userName,
			});

			if (!result.success) {
				if (
					result.error &&
					!result.error.includes("cancelado")
				) {
					notify({
						type: "error",
						title: "Falha ao salvar no Corel",
						message: result.error || "Nao foi possivel salvar o arquivo.",
						duration: 6000,
					});
				}
				return;
			}

			// O arquivo ja foi salvo neste ponto. A pergunta de fechamento do
			// documento e uma decisao opcional pos-salvamento, portanto o estado
			// "Salvando..." deve ser encerrado antes de aguardar essa confirmacao
			// — caso contrario o botao fica preso enquanto a Promise nao resolve.
			setIsQuickSaving(false);
			setIsClosingModalOpen(false);
			setIsPixModalOpen(false);
			onOrderCreated?.(createdOrderPayload);

			sendToTerminal(
				"[QUOTATION:QUICK_SAVE] Solicitando confirmacao para fechar arquivo no Corel",
				{ orcId: createdOrderPayload.orcId, oitId: createdOrderPayload.oitId },
				LogCategory.BUSINESS,
			);
			const shouldCloseDocument = await requestConfirmation({
				title: "Fechar arquivo no Corel?",
				message:
					"O arquivo foi salvo no Gerenciador. Deseja fechar o documento ativo no CorelDRAW agora?",
				confirmLabel: "Fechar arquivo",
				cancelLabel: "Manter aberto",
			});

			if (shouldCloseDocument) {
				const closeResult = await DocumentService.closeDocument({
					waitForResponse: true,
					timeoutMs: 10_000,
					useFallback: true,
					fallbackDelayMs: 900,
				});

				if (closeResult.status !== "ok" && closeResult.status !== "timeout") {
					notify({
						type: "warning",
						title: "Arquivo salvo, mas nao fechado",
						message:
							closeResult.message ||
							"Nao foi possivel confirmar o fechamento do documento no Corel.",
						duration: 5000,
					});
				}
			}

			notify({
				type: "success",
				title: "Arquivo salvo no Gerenciador",
				message: `Arquivo salvo no Gerenciador — orcamento #${createdOrderPayload.orcId} mantido.`,
				duration: 5000,
			});
		} finally {
			setIsQuickSaving(false);
		}
	}, [
		createdOrderPayload,
		notify,
		objects,
		onOrderCreated,
		selectedClient,
		selectedQuality,
		selectedRule,
	]);

	const handleCopyPixCode = useCallback(() => {
		if (!pixData?.brcode) {
			notify({
				type: "warning",
				title: "PIX nao disponivel",
				message: "Gere o PIX antes de copiar o codigo Pix Copia e Cola.",
				duration: 4000,
			});
			return;
		}

		navigator.clipboard
			.writeText(pixData.brcode)
			.then(() => {
				setPixCopiedFeedback(true);
				setHasCopiedPix(true);
				setTimeout(() => setPixCopiedFeedback(false), 2000);

				notify({
					type: "success",
					title: "Pix Copia e Cola copiado",
					message: "Codigo PIX copiado para a area de transferencia.",
					duration: 3000,
				});
			})
			.catch((err) => {
				const errorMessage =
					err instanceof Error ? err.message : "Erro ao copiar codigo PIX";
				sendToTerminal(
					"[QUOTATION] Erro ao copiar BR Code PIX",
					{ error: errorMessage },
					LogCategory.ERROR,
				);

				notify({
					type: "error",
					title: "Erro ao copiar PIX",
					message: "Nao foi possivel copiar o codigo PIX.",
					duration: 4000,
				});
			});
	}, [notify, pixData]);

	const handleCreateOrder = useCallback(async () => {
		if (createdOrderData) {
			openClosingModal();
			return;
		}

		if (!quotationResult) {
			sendToTerminal(
				"[QUOTATION] Registro de orcamento bloqueado: orcamento nao calculado",
				{},
				LogCategory.ERROR,
			);
			notify({
				type: "warning",
				title: "Atencao",
				message: "Calcule o orcamento antes de criar o pedido.",
				duration: 4000,
			});
			return;
		}

		if (!selectedClient?.id) {
			sendToTerminal(
				"[QUOTATION] Registro de orcamento bloqueado: cliente ausente",
				{},
				LogCategory.ERROR,
			);
			notify({
				type: "warning",
				title: "Cliente obrigatorio",
				message: "Selecione um cliente cadastrado para criar o pedido.",
				duration: 5000,
			});
			return;
		}

		if (!savingRule?.umd_id || !selectedRule?.produto?.id) {
			sendToTerminal(
				"[QUOTATION] Registro de orcamento bloqueado: regra/material invalidos",
				{
					hasSavingRule: !!savingRule,
					unitId: savingRule?.umd_id ?? null,
					productId: selectedRule?.produto?.id ?? null,
				},
				LogCategory.ERROR,
			);
			notify({
				type: "error",
				title: "Dados incompletos",
				message: "Material/regra invalidos para criacao do pedido.",
				duration: 5000,
			});
			return;
		}

		const draftError = validateDraftForm();
		if (draftError) {
			setCreateError(draftError);
			notify({
				type: "warning",
				title: "Revise os dados do fechamento",
				message: draftError,
				duration: 5000,
			});
			return;
		}

		// Cliente roteado: o pedido nasce no tenant destino, então o usuário
		// cadastrante tem que ser o usuário resolvido no destino (do token de
		// operação), não o da sessão global (que não existe no banco destino).
		const operationContext = QuotationOperationContextStore.get();
		const userData = TokenService.getUserData();
		const userId = operationContext
			? operationContext.routing.targetUserId
			: Number(userData?.data?.usuario?.id);
		if (!Number.isFinite(userId) || userId <= 0) {
			sendToTerminal(
				"[QUOTATION] Registro de orcamento bloqueado: usuario invalido na sessao",
				{
					rawUserId: userData?.data?.usuario?.id ?? null,
					routed: Boolean(operationContext),
				},
				LogCategory.ERROR,
			);
			notify({
				type: "error",
				title: "Sessao invalida",
				message: "Nao foi possivel identificar o usuario logado.",
				duration: 5000,
			});
			return;
		}

		const quantity =
			finalMetragem !== null
				? finalMetragem
				: isMetros
					? totalMetragem
					: totalUnidades;
		const categoriaItem = selectedRule.produto?.categoria_item ?? 1;

		try {
			setIsCreatingOrder(true);
			setCreateError(null);
			sendToTerminal(
				"[QUOTATION] Iniciando registro de orcamento na tela de resumo",
				{
					clientId: Number(selectedClient.id),
					userId,
					productId: Number(selectedRule.produto.id),
					quantity,
					unitId: savingRule.umd_id,
					unitPrice: quotationResult.preco_unitario,
					totalValue: quotationResult.valor_total,
					autoGeneratePix,
					entrega: closingForm.entrega,
				},
				LogCategory.BUSINESS,
			);

			const createdOrder = await createOrcamentoFromQuotation({
				clientId: Number(selectedClient.id),
				userId,
				productId: Number(selectedRule.produto.id),
				productName: selectedRule.produto?.nome_servico || "Material",
				produtoType: categoriaItem,
				materialTypeId: selectedQuality?.id ? Number(selectedQuality.id) : null,
				unitId: savingRule.umd_id,
				quantity,
				unitPrice: quotationResult.preco_unitario,
				totalValue: quotationResult.valor_total,
				discountValue: descontoAplicado > 0 ? descontoAplicado : 0,
				observacaoPedido:
					closingForm.observacaoPedido.trim() ||
					`Orcamento criado via Corel (${selectedQuality?.one1_nome || "sem qualidade"})`,
				observacaoItem: closingForm.observacaoItem.trim(),
				exibirRecibo: closingForm.exibirRecibo,
				formaPagamentoId: selectedPaymentMethodId,
			});

			sendToTerminal(
				"[QUOTATION] Orcamento registrado a partir do resumo",
				{
					orcId: createdOrder.orcId,
					oitId: createdOrder.oitId,
					cliId: selectedClient.id,
					proId: selectedRule.produto.id,
				},
				LogCategory.BUSINESS,
			);

			// Cliente roteado: persiste o vínculo origem->destino no tenant destino.
			// Não bloqueia o fluxo do pedido se a gravação do vínculo falhar.
			if (
				QuotationOperationContextStore.isActive() &&
				createdOrder.oitId !== null
			) {
				try {
					await persistCrossTenantLink({
						targetClientId: Number(selectedClient.id),
						targetOrcId: createdOrder.orcId,
						targetOitId: createdOrder.oitId,
						// Pedido de origem confirmado no gate (modal) antes da criação.
						// Contribuinte primário (retrocompat 1:1) + lista completa N->1.
						sourceOrcId: sourceLink[0]?.orcId ?? null,
						sourceOitId: sourceLink[0]?.oitId ?? null,
						sources: sourceLink.map((s) => ({
							sourceOrcId: s.orcId,
							sourceOitId: s.oitId,
							sourceRole: s.role ?? null,
						})),
					});

					// Vínculo OK → move os itens de ORIGEM para "Aguardando Produção
					// Terceirizada" (status 14), com o token NORMAL (origem). SPEC-17.
					// Best-effort: falha aqui NÃO desfaz o vínculo, apenas avisa —
					// o status pode ser corrigido depois.
					try {
						const items = sourceLink.map((s) => ({
							orcId: s.orcId,
							oitId: s.oitId,
						}));
						if (items.length > 0) {
							const res = await markSourceItemsOutsourced(items);
							if (res.missing.length > 0) {
								notify({
									type: "warning",
									title: "Terceirização",
									message: `${res.missing.length} item(ns) de origem não encontrado(s) para atualizar o status.`,
								});
							}
						}
					} catch (statusError) {
						sendToTerminal(
							"[QUOTATION] Vínculo salvo, mas falha ao mover status de origem para 14 (não crítico)",
							{
								error:
									statusError instanceof Error
										? statusError.message
										: String(statusError),
							},
							LogCategory.ERROR,
						);
						notify({
							type: "warning",
							title: "Terceirização",
							message:
								"O vínculo foi salvo, mas o status do item na origem não pôde ser atualizado. Ajuste manualmente se necessário.",
						});
					}
				} catch (linkError) {
					sendToTerminal(
						"[QUOTATION] Falha ao persistir vínculo cross-tenant (não crítico)",
						{
							error:
								linkError instanceof Error
									? linkError.message
									: String(linkError),
						},
						LogCategory.ERROR,
					);
				}
			}

			const runtimeData: CreatedOrderRuntimeData = {
				orcId: createdOrder.orcId,
				oitId: createdOrder.oitId,
				clientId: Number(selectedClient.id),
				userId,
				totalValue: quotationResult.valor_total,
			};

			setCreatedOrderData(runtimeData);
			setClosingState("registered");
			setPixData(null);
			setPixError(null);
			setPixCopiedFeedback(false);

			const payload: CreatedQuotationOrderData = {
				orcId: createdOrder.orcId,
				oitId: createdOrder.oitId,
				clientId: Number(selectedClient.id),
				clientName:
					finalClientName ||
					selectedClient.empresa ||
					selectedClient.nome ||
					"Cliente",
				productId: Number(selectedRule.produto.id),
				productName: selectedRule.produto?.nome_servico || "Material",
				categoriaItem,
				unitId: savingRule.umd_id,
				quantity,
				unitPrice: quotationResult.preco_unitario,
				totalValue: quotationResult.valor_total,
				qualityName: selectedQuality?.one1_nome || null,
				autoOpenPipeline: false,
				observacaoPedido: closingForm.observacaoPedido.trim(),
				observacaoItem: closingForm.observacaoItem.trim(),
				entrega: closingForm.entrega,
				exibirRecibo: closingForm.exibirRecibo,
				formaPagamentoId: selectedPaymentMethodId,
				formaPagamentoDescricao: selectedPaymentMethod?.descricao || null,
			};

			setCreatedOrderPayload(payload);
			setCreateError(null);
			setClosingDataError(null);
			setIsClosingModalOpen(true);

			if (!autoGeneratePix) {
				notify({
					type: "success",
					title: "Orcamento registrado",
					message: `Orcamento #${createdOrder.orcId} registrado. Complete o fechamento para continuar.`,
					duration: 4500,
				});
			}

			sendToTerminal(
				"[QUOTATION] Orcamento registrado, aguardando fechamento do pedido",
				{
					orcId: createdOrder.orcId,
					autoGeneratePix,
					formaPagamentoId: selectedPaymentMethodId,
				},
				LogCategory.BUSINESS,
			);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Falha ao registrar orcamento";
			setCreateError(errorMessage);
			sendToTerminal(
				"[QUOTATION] Erro ao registrar orcamento",
				{
					error: errorMessage,
					cliId: selectedClient.id,
					proId: selectedRule.produto.id,
				},
				LogCategory.ERROR,
			);
			notify({
				type: "error",
				title: "Erro ao registrar orcamento",
				message: errorMessage,
				duration: 7000,
			});
		} finally {
			setIsCreatingOrder(false);
		}
	}, [
		autoGeneratePix,
		closingForm.entrega,
		closingForm.exibirRecibo,
		closingForm.observacaoItem,
		closingForm.observacaoPedido,
		createdOrderData,
		descontoAplicado,
		finalClientName,
		finalMetragem,
		isMetros,
		notify,
		openClosingModal,
		onOrderCreated,
		quotationResult,
		savingRule,
		selectedClient,
		selectedPaymentMethod,
		selectedPaymentMethodId,
		selectedQuality,
		selectedRule,
		sourceLink,
		totalMetragem,
		totalUnidades,
		canCreateOrder,
		validateDraftForm,
	]);

	return {
		autoGeneratePix,
		setAutoGeneratePix,
		isCreatingOrder,
		isGeneratingPix,
		isSavingClosingData,
		isQuickSaving,
		isUploadingComprovante,
		createdOrderData,
		createdOrderPayload,
		sourceLink,
		setSourceLink,
		closingState,
		isClosingModalOpen,
		setIsClosingModalOpen,
		closingForm,
		createError,
		closingDataError,
		hasSavedClosingData,
		paymentMethods,
		selectedPaymentMethodId,
		selectedPaymentMethod,
		isLoadingPaymentMethods,
		paymentMethodsError,
		isProcessingFinancial,
		pixData,
		pixError,
		pixCopiedFeedback,
		pixQrImageUrl,
		comprovanteStatus,
		comprovanteError,
		comprovanteUploadedCount,
		isPixModalOpen,
		setIsPixModalOpen,
		isPixPaymentMethodSelected,
		hasSelectedClientDocument,
		hasGeneratedPix,
		hasCopiedPix,
		paymentMethodHint,
		closingActionHint,
		saveNowHint,
		canGeneratePix,
		canSaveClosing,
		saveClosingHint,
		generatePixHint,
		canProcessFinancial,
		canQuickSave,
		resetClosingFlowState,
		resetClosingDetailsPreservingPayment,
		setClosingField,
		removeClosingFile,
		openClosingModal,
		handleRetryComprovante,
		handleSelectPaymentMethod,
		handleSaveClosingData,
		handleGeneratePix,
		handleProcessFinancialApproval,
		handleQuickSaveToGerenciador,
		handleCopyPixCode,
		handleCreateOrder,
		paymentFlowKind,
		showComprovanteSection,
		comprovanteRequired,
		closingChecklist,
		closingStatusLabel,
		comprovanteSectionTooltip,
	};
}
